import { createContext, ReactNode, useCallback, useContext, useEffect, useMemo, useRef } from 'react';

export type OnResizeCallback = (dimensions: Pick<DOMRect, 'height' | 'width'>) => void;

export interface IResizeNotifierContext {
    subscribe(element: Element | Node, callback: OnResizeCallback): void;
    unsubscribe(element: Element | Node, callback: OnResizeCallback): void;
}

function NOOP() {
    throw new Error('Not Implemented!!!');
}

export const ResizeNotifierContext = createContext<IResizeNotifierContext>({
    subscribe: NOOP,
    unsubscribe: NOOP
});

export interface IResizeNotifierProviderProps {
    children?: ReactNode | ReactNode[];
}


export function ResizeNotifierProvider({ children }: IResizeNotifierProviderProps) {
    const subscriptionPoolRef = useRef(new WeakMap<Element, OnResizeCallback[]>());
    const onResize = useCallback((entries: ResizeObserverEntry[]) => {
        const { current: subscriptionPool } = subscriptionPoolRef;
        entries.forEach((entry) => {
            const callbacks = subscriptionPool.get(entry.target);
            callbacks?.forEach((cb) => cb(entry.contentRect));
        });
    }, []);
    const resizeObserverRef = useRef(new ResizeObserver(onResize));

    useEffect(() => () => resizeObserverRef.current.disconnect(), []);

    const subscribe = useCallback((element: Element, callback: OnResizeCallback) => {
        const { current: subscriptionPool } = subscriptionPoolRef;
        let callbacks = subscriptionPool.get(element);
        if (!callbacks) {
            callbacks = [];
        }
        callbacks.push(callback);
        subscriptionPool.set(element, callbacks);
        resizeObserverRef.current.observe(element);
    }, []);

    const unsubscribe = useCallback((element: Element, callback: OnResizeCallback) => {
        const { current: subscriptionPool } = subscriptionPoolRef;
        resizeObserverRef.current.unobserve(element);
        const callbacks = subscriptionPool.get(element);
        if (!callbacks) {
            return;
        }
        const newCallbacks = callbacks.filter(cb => cb !== callback);
        if (newCallbacks.length) {
            subscriptionPool.set(element, newCallbacks);
            return;
        }
        subscriptionPool.delete(element);
    }, []);

    const contextValue = useMemo(() => ({ subscribe, unsubscribe }), [subscribe, unsubscribe]);

    return (
        <ResizeNotifierContext.Provider value={contextValue}>
            {children}
        </ResizeNotifierContext.Provider>
    )
}

export function useResizeNotifier() {
    return useContext(ResizeNotifierContext);
}
